import { useEffect, useState } from "react";
import { apiConfigured } from "../api";
import type { AuditPortalQuery } from "./collaboration-model";
import { portalApi, prepareEvidence } from "./collaboration-api";
import "./collaboration.css";

export function AuditPortal({ token }: { token: string }) {
  const [query, setQuery] = useState<AuditPortalQuery | null>(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const validToken = /^[A-Za-z0-9_-]{32,128}$/.test(token);
  useEffect(() => {
    let live = true;
    setQuery(null);
    setText("");
    setFile(null);
    setNotice("");
    setError("");
    setBusy(true);
    if (!validToken || !apiConfigured) {
      setError(
        "This link is unavailable or expired. Please request a new link from your audit contact.",
      );
      setBusy(false);
      return;
    }
    portalApi
      .get(token)
      .then((value) => {
        if (live) setQuery(value);
      })
      .catch((failure) => {
        if (live)
          setError(
            failure instanceof Error
              ? failure.message
              : "This query is unavailable. Please contact your audit team.",
          );
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, [token, validToken]);
  const run = async (action: () => Promise<void>, message = "") => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(message);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "The request could not be completed. Your input has been kept.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="ac-root ac-portal">
      <header className="ac-header">
        <div>
          <span className="ac-eyebrow">LEDZERLY · CLIENT RESPONSE</span>
          <h1>Audit query</h1>
          <p>A restricted space to respond to your audit team.</p>
        </div>
        <span className="ac-badge">Client access link</span>
      </header>
      <div className="ac-notice">
        Keep this link private. Anyone with an active copy can access this
        query. Responses are recorded as an unverified client link holder.
      </div>
      {error && (
        <div role="alert" className="ac-error">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="ac-success">
          {notice}
        </div>
      )}
      {busy && <p role="status">Loading or saving your response…</p>}
      {!query && !busy && (
        <p>Please contact the audit team if you need a replacement link.</p>
      )}
      {query && (
        <fieldset disabled={busy} className="ac-controls">
          <section className="ac-panel">
            <span className="ac-eyebrow">{query.reference}</span>
            <h2>{query.title}</h2>
            <p className="ac-question">{query.description}</p>
            <dl className="ac-metadata">
              <div>
                <dt>Area</dt>
                <dd>{query.area}</dd>
              </div>
              <div>
                <dt>Contact</dt>
                <dd>{query.clientContact}</dd>
              </div>
              <div>
                <dt>Response due</dt>
                <dd>{query.dueDate}</dd>
              </div>
            </dl>
            <button
              onClick={() =>
                run(
                  async () => setQuery(await portalApi.get(token)),
                  "Query refreshed. Any unsent response remains below.",
                )
              }
            >
              Refresh query
            </button>
          </section>
          <section className="ac-panel">
            <h2>Shared discussion</h2>
            <ol className="ac-thread">
              {query.messages.length ? (
                query.messages.map((message) => (
                  <li className="ac-client" key={message.id}>
                    <div>
                      <strong>{message.author}</strong>
                      <time dateTime={message.at}>
                        {new Date(message.at).toLocaleString()}
                      </time>
                    </div>
                    <p>{message.text}</p>
                  </li>
                ))
              ) : (
                <li>No shared messages yet.</li>
              )}
            </ol>
            {query.acceptingResponses ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void run(async () => {
                    const updated = await portalApi.reply(token, text);
                    setQuery(updated);
                    setText("");
                  }, "Response saved for the audit team. The team will assess whether further evidence is needed.");
                }}
              >
                <label className="ac-field">
                  <span>Your response</span>
                  <textarea
                    required
                    maxLength={10000}
                    rows={5}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </label>
                <button className="ac-primary">Submit response</button>
              </form>
            ) : (
              <p className="ac-notice">
                This query is not accepting further responses. Contact your
                audit team if more information needs to be added.
              </p>
            )}
          </section>
          <section className="ac-panel">
            <h2>Submitted evidence</h2>
            <p>
              PDF, PNG or JPEG only, up to 5 MB per file. Files are submitted
              for audit review; uploading does not mean the evidence has been
              accepted. Files are not scanned for malware; check downloads
              before opening.
            </p>
            <div className="ac-files">
              {query.attachments.map((attachment) => (
                <div className="ac-file" key={attachment.id}>
                  <strong>{attachment.name}</strong>
                  <span>
                    {(attachment.size / 1024).toFixed(1)} KB ·{" "}
                    {new Date(attachment.uploadedAt).toLocaleString()}
                  </span>
                  <code>SHA-256 {attachment.sha256}</code>
                  <button
                    onClick={() =>
                      run(async () =>
                        portalApi.download(
                          token,
                          attachment.id,
                          attachment.name,
                        ),
                      )
                    }
                  >
                    Download {attachment.name}
                  </button>
                </div>
              ))}
            </div>
            {!query.attachments.length && <p>No evidence submitted yet.</p>}
            {query.acceptingResponses && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!file) return;
                  void run(async () => {
                    const prepared = await prepareEvidence(file);
                    setQuery(await portalApi.upload(token, prepared));
                    setFile(null);
                    setFileKey((value) => value + 1);
                  }, "Evidence submitted for review.");
                }}
              >
                <label className="ac-field">
                  <span>Evidence file</span>
                  <input
                    key={fileKey}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                <button disabled={!file}>Submit evidence</button>
              </form>
            )}
          </section>
        </fieldset>
      )}
      <footer className="ac-footer">
        This link provides access to one audit query. For questions about the
        request or your documents, contact your audit team.
      </footer>
    </main>
  );
}
